import { Spinner } from '../../components/Spinner.js';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { apiClient } from '../../lib/api-client.js';
import { UserRankBadge } from '../../components/UserRankBadge.js';

interface FollowUser {
  id: string;
  nickname: string;
  avatarUrl: string | null;
  checkinRank: string;
  spotMakerRank: string;
}

interface Props {
  type: 'followers' | 'following';
}

export function FollowListView({ type }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [users, setUsers] = useState<FollowUser[]>([]);
  const [loading, setLoading] = useState(true);

  const title = type === 'followers' ? 'フォロワー' : 'フォロー中';

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiClient
      .get<FollowUser[]>(`/users/${id}/${type}`)
      .then((data) => {
        if (data) setUsers(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, type]);

  return (
    <div>
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 flex items-center gap-2 px-4 py-3">
        <button onClick={() => navigate(-1)} className="text-gray-500 p-1 -ml-1">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="text-base font-bold text-gray-900">{title}</h1>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <Spinner />
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400 text-sm gap-2">
          <span className="text-3xl">👤</span>
          <p>{type === 'followers' ? 'フォロワーはいません' : 'フォロー中のユーザーはいません'}</p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {users.map((user) => (
            <li key={user.id}>
              <Link
                to={`/users/${user.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                  {user.avatarUrl && (
                    <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <span className="flex-1 text-sm font-medium text-gray-800">{user.nickname}</span>
                <div className="flex gap-1.5">
                  <UserRankBadge
                    rank={user.checkinRank}
                    type="checkin"
                    size="sm"
                    showLabel={false}
                  />
                  <UserRankBadge
                    rank={user.spotMakerRank}
                    type="spotMaker"
                    size="sm"
                    showLabel={false}
                  />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
